/**
 * Admin Marketing — Studio API.
 * `/api/admin/v2/marketing/{content,variants,distribution,calendar}`
 *
 * The state machine is server-side. There is deliberately no
 * `useSetPostStatusMutation` here: `status` is not a writable field, so a PATCH
 * carrying it is dropped rather than honoured. Movement happens through
 * submit / approve / retry, each of which validates the current state and
 * records who acted — and each of which is a separate hook below so a UI cannot
 * accidentally express "just set it to published".
 *
 * Publishing is not exposed at all. Approving a post with a `scheduled_at` moves
 * it to `scheduled`, and the Celery beat tick claims it from there. That single
 * publish path is what makes double-posting preventable.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  ApiCalendar,
  ApiContentItem,
  ApiContentVariant,
  ApiOutletEmail,
  ApiOutletEmailThread,
  ApiPlacement,
  ApiPostIdea,
  ApiPostTemplate,
  ApiQueueResult,
  ApiSocialPost,
  MarketingList,
  MarketingListParams,
} from '@/client/types/marketing/api';
import { MARKETING_BASE, marketingKeys } from './keys';

const studioKeys = {
  all: ['marketing', 'studio'] as const,
  content: (p: unknown = {}) => ['marketing', 'studio', 'content', p] as const,
  contentItem: (id: number) => ['marketing', 'studio', 'content', 'item', id] as const,
  variants: (mciid: number) => ['marketing', 'studio', 'content', mciid, 'variants'] as const,
  social: (p: unknown = {}) => ['marketing', 'studio', 'social', p] as const,
  socialItem: (id: number) => ['marketing', 'studio', 'social', 'item', id] as const,
  traditional: (p: unknown = {}) => ['marketing', 'studio', 'traditional', p] as const,
  outletEmails: (p: unknown = {}) => ['marketing', 'studio', 'outlet-emails', p] as const,
  outletEmailThread: (meid: number) =>
    ['marketing', 'studio', 'outlet-emails', 'thread', meid] as const,
  calendar: (p: unknown = {}) => ['marketing', 'studio', 'calendar', p] as const,
  ideas: (p: unknown = {}) => ['marketing', 'studio', 'ideas', p] as const,
  templates: (p: unknown = {}) => ['marketing', 'studio', 'templates', p] as const,
};

export { studioKeys };

/* --------------------------------------------------------------- content */

export const useContentQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiContentItem>>>({
    defaultErrorMessage: 'Could not load content',
    endpoint: `${MARKETING_BASE}/content`,
  });
  return useQuery({
    queryKey: studioKeys.content(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export const useContentItemQuery = (mciid: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiContentItem>>({
    defaultErrorMessage: 'Could not load the content item',
    endpoint: `${MARKETING_BASE}/content/${mciid}`,
  });
  return useQuery({
    queryKey: studioKeys.contentItem(mciid),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(mciid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

export const useCreateContentMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiContentItem>, AdminEnvelope<ApiContentItem>>({
    defaultErrorMessage: 'Could not create the content item',
    endpoint: `${MARKETING_BASE}/content`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useUpdateContentMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiContentItem> & { mciid: number },
    AdminEnvelope<ApiContentItem>
  >({
    defaultErrorMessage: 'Could not update the content item',
    endpoint: (p) => `${MARKETING_BASE}/content/${p.mciid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useDeleteContentMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mciid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the content item',
    endpoint: (p) => `${MARKETING_BASE}/content/${p.mciid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/**
 * Move between the *non-gated* statuses only — idea / draft / archived.
 * Anything gated is refused by the server with `invalid_state` naming the action
 * to use instead.
 */
export const useSetContentStatusMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { mciid: number; status: 'idea' | 'draft' | 'archived' },
    AdminEnvelope<ApiContentItem>
  >({
    defaultErrorMessage: 'Could not change the status',
    endpoint: (p) => `${MARKETING_BASE}/content/${p.mciid}/status`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/** Send for review. Snapshots the body so the approver's diff is real. */
export const useSubmitContentMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mciid: number }, AdminEnvelope<ApiContentItem>>({
    defaultErrorMessage: 'Could not submit for review',
    endpoint: (p) => `${MARKETING_BASE}/content/${p.mciid}/submit`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/** Requires `marketing.approve`. An author cannot clear their own work. */
export const useApproveContentMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mciid: number }, AdminEnvelope<ApiContentItem>>({
    defaultErrorMessage: 'Could not approve',
    endpoint: (p) => `${MARKETING_BASE}/content/${p.mciid}/approve`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/**
 * Fan variants out into distribution rows.
 *
 * Safe to call twice: only variants not already queued are materialised, so
 * adding one variant and re-queueing creates one post rather than duplicating
 * the whole fan-out.
 */
export const useQueueVariantsMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mciid: number }, AdminEnvelope<ApiQueueResult>>({
    defaultErrorMessage: 'Could not queue the variants',
    endpoint: (p) => `${MARKETING_BASE}/content/${p.mciid}/queue`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/* -------------------------------------------------------------- variants */

/**
 * The variants authored for one content item.
 *
 * `queued_at` marks the ones already materialised into a distribution row.
 * Re-queueing skips those by design — show the distinction, or the skip reads
 * as a failure.
 */
export const useContentVariantsQuery = (mciid: number, enabled = true) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{ mciid: number; count: number; items: ApiContentVariant[] }>
  >({
    defaultErrorMessage: 'Could not load the variants',
    endpoint: `${MARKETING_BASE}/content/${mciid}/variants`,
  });
  return useQuery({
    queryKey: studioKeys.variants(mciid),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(mciid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

export const useCreateVariantMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiContentVariant>, AdminEnvelope<ApiContentVariant>>({
    defaultErrorMessage: 'Could not add the variant',
    endpoint: `${MARKETING_BASE}/variants`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useUpdateVariantMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiContentVariant> & { mcvid: number },
    AdminEnvelope<ApiContentVariant>
  >({
    defaultErrorMessage: 'Could not update the variant',
    endpoint: (p) => `${MARKETING_BASE}/variants/${p.mcvid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useDeleteVariantMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mcvid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the variant',
    endpoint: (p) => `${MARKETING_BASE}/variants/${p.mcvid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/* ---------------------------------------------------- distribution: social */

export const useSocialPostsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiSocialPost>>>({
    defaultErrorMessage: 'Could not load the publish queue',
    endpoint: `${MARKETING_BASE}/distribution/social`,
  });
  return useQuery({
    queryKey: studioKeys.social(params),
    queryFn: () => queryFn({ ...params }),
    // The queue moves on its own: the beat tick flips scheduled -> publishing ->
    // published without anyone touching the page, so it goes stale faster than a
    // list a human alone edits.
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export const useSocialPostQuery = (mspid: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSocialPost>>({
    defaultErrorMessage: 'Could not load the post',
    endpoint: `${MARKETING_BASE}/distribution/social/${mspid}`,
  });
  return useQuery({
    queryKey: studioKeys.socialItem(mspid),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(mspid),
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

export const useCreateSocialPostMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiSocialPost>, AdminEnvelope<ApiSocialPost>>({
    defaultErrorMessage: 'Could not create the post',
    endpoint: `${MARKETING_BASE}/distribution/social`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useUpdateSocialPostMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiSocialPost> & { mspid: number },
    AdminEnvelope<ApiSocialPost>
  >({
    defaultErrorMessage: 'Could not update the post',
    endpoint: (p) => `${MARKETING_BASE}/distribution/social/${p.mspid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useDeleteSocialPostMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mspid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the post',
    endpoint: (p) => `${MARKETING_BASE}/distribution/social/${p.mspid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useSubmitPostMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mspid: number }, AdminEnvelope<ApiSocialPost>>({
    defaultErrorMessage: 'Could not submit for approval',
    endpoint: (p) => `${MARKETING_BASE}/distribution/social/${p.mspid}/submit`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/**
 * Approve. Requires `marketing.approve`.
 *
 * Moves to `scheduled` when the post carries a time, `queued` when it does not —
 * only `scheduled` is claimed by the worker, so approving something undated
 * parks it rather than firing it immediately.
 */
export const useApprovePostMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mspid: number }, AdminEnvelope<ApiSocialPost>>({
    defaultErrorMessage: 'Could not approve the post',
    endpoint: (p) => `${MARKETING_BASE}/distribution/social/${p.mspid}/approve`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/** Requeue a failed post, clearing the failure and the attempt counter. */
export const useRetryPostMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mspid: number }, AdminEnvelope<ApiSocialPost>>({
    defaultErrorMessage: 'Could not retry the post',
    endpoint: (p) => `${MARKETING_BASE}/distribution/social/${p.mspid}/retry`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useReorderPostsMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ order: number[] }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not reorder the queue',
    endpoint: `${MARKETING_BASE}/distribution/social/reorder`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/* ----------------------------------------------- distribution: traditional */

export const usePlacementsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiPlacement>>>({
    defaultErrorMessage: 'Could not load placements',
    endpoint: `${MARKETING_BASE}/distribution/traditional`,
  });
  return useQuery({
    queryKey: studioKeys.traditional(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export const useCreatePlacementMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiPlacement>, AdminEnvelope<ApiPlacement>>({
    defaultErrorMessage: 'Could not create the placement',
    endpoint: `${MARKETING_BASE}/distribution/traditional`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useUpdatePlacementMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiPlacement> & { mtpid: number },
    AdminEnvelope<ApiPlacement>
  >({
    defaultErrorMessage: 'Could not update the placement',
    endpoint: (p) => `${MARKETING_BASE}/distribution/traditional/${p.mtpid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useDeletePlacementMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mtpid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the placement',
    endpoint: (p) => `${MARKETING_BASE}/distribution/traditional/${p.mtpid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/**
 * Move a placement through its gate: submit → approve → published.
 *
 * `published` is a third action here and deliberately not on social posts: a
 * social post publishes itself when the beat tick claims it, but we only *pitch*
 * a traditional placement. The outlet decides whether it runs, so somebody has
 * to record that it did.
 */
export const useTransitionPlacementMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    {
      mtpid: number;
      action: 'submit' | 'approve' | 'published';
      url?: string;
      published_at?: string;
    },
    AdminEnvelope<ApiPlacement>
  >({
    defaultErrorMessage: 'Could not update the placement',
    endpoint: (p) => `${MARKETING_BASE}/distribution/traditional/${p.mtpid}/${p.action}`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studioKeys.all });
      // A published placement becomes owned coverage and counts toward its play.
      qc.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};

/* ---------------------------------------------------------- outlet emails */

/**
 * Emails to an outlet about a placement. Every one is a draft until someone
 * with `marketing.approve` approves it, and approving is what sends it — from
 * a shared mailbox, so the outlet's reply lands in the Communications inbox.
 */
export const useOutletEmailsQuery = (
  params: MarketingListParams = {},
  options: { enabled?: boolean } = {},
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiOutletEmail>>>({
    defaultErrorMessage: 'Could not load outlet emails',
    endpoint: `${MARKETING_BASE}/outlet-emails`,
  });
  return useQuery({
    queryKey: studioKeys.outletEmails(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: options.enabled ?? true,
    select: (r) => r?.data,
  });
};

export const useOutletEmailThreadQuery = (meid: number | null) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiOutletEmailThread>>({
    defaultErrorMessage: 'Could not load the email thread',
    endpoint: `${MARKETING_BASE}/outlet-emails/${meid}/thread`,
  });
  return useQuery({
    queryKey: studioKeys.outletEmailThread(meid ?? 0),
    queryFn: () => queryFn({}),
    enabled: meid != null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

export type OutletEmailInput = Pick<
  ApiOutletEmail,
  'kind' | 'to_email' | 'to_name' | 'subject' | 'body' | 'from_mailbox'
> & { mtpid?: number | null; moid?: number };

export const useCreateOutletEmailMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<OutletEmailInput, AdminEnvelope<ApiOutletEmail>>({
    defaultErrorMessage: 'Could not save the email',
    endpoint: `${MARKETING_BASE}/outlet-emails`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useUpdateOutletEmailMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<OutletEmailInput> & { meid: number },
    AdminEnvelope<ApiOutletEmail>
  >({
    defaultErrorMessage: 'Could not save the email',
    endpoint: (p) => `${MARKETING_BASE}/outlet-emails/${p.meid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useDeleteOutletEmailMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ meid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not delete the email',
    endpoint: (p) => `${MARKETING_BASE}/outlet-emails/${p.meid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/** submit → pending approval; approve → sends it (needs `marketing.approve`). */
export const useTransitionOutletEmailMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { meid: number; action: 'submit' | 'approve' },
    AdminEnvelope<ApiOutletEmail>
  >({
    defaultErrorMessage: 'Could not update the email',
    endpoint: (p) => `${MARKETING_BASE}/outlet-emails/${p.meid}/${p.action}`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studioKeys.all });
      // A sent email logs a row on the Outreach tracker.
      qc.invalidateQueries({ queryKey: marketingKeys.sources.all });
    },
  });
};

/**
 * Move many social posts at once.
 *
 * Transitions only — submit | approve | retry. There is no bulk status set, for
 * the same reason there is no single one: it would skip the approver stamp
 * twelve at a time. Failures come back per id, so render partial success rather
 * than assuming all-or-nothing.
 */
export const useBulkTransitionPostsMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { ids: number[]; action: 'submit' | 'approve' | 'retry' },
    AdminEnvelope<{
      action: string;
      updated: number[];
      failed: { mspid: number; error: string }[];
    }>
  >({
    defaultErrorMessage: 'Could not update the posts',
    endpoint: `${MARKETING_BASE}/distribution/social/bulk`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

/* ----------------------------------------------------- ideas + templates */

export const usePostIdeasQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiPostIdea>>>({
    defaultErrorMessage: 'Could not load ideas',
    endpoint: `${MARKETING_BASE}/ideas`,
  });
  return useQuery({
    queryKey: studioKeys.ideas(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

export const useCreatePostIdeaMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiPostIdea>, AdminEnvelope<ApiPostIdea>>({
    defaultErrorMessage: 'Could not save the idea',
    endpoint: `${MARKETING_BASE}/ideas`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const useDeletePostIdeaMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ piid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the idea',
    endpoint: (p) => `${MARKETING_BASE}/ideas/${p.piid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: studioKeys.all }),
  });
};

export const usePostTemplatesQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiPostTemplate>>>({
    defaultErrorMessage: 'Could not load templates',
    endpoint: `${MARKETING_BASE}/templates`,
  });
  return useQuery({
    queryKey: studioKeys.templates(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

/* -------------------------------------------------------------- calendar */

/** Content, posts and placements on one feed — three lists cannot be merged
 *  correctly client-side once each is independently paginated. */
export const useCalendarQuery = (params: { start?: string; end?: string } = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiCalendar>>({
    defaultErrorMessage: 'Could not load the calendar',
    endpoint: `${MARKETING_BASE}/calendar`,
  });
  return useQuery({
    queryKey: studioKeys.calendar(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

/** Drag-to-reschedule. `kind` is content | social | traditional. */
export const useRescheduleMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { kind: 'content' | 'social' | 'traditional'; id: number; scheduled_at: string | null },
    AdminEnvelope<unknown>
  >({
    defaultErrorMessage: 'Could not reschedule',
    endpoint: (p) => `${MARKETING_BASE}/calendar/${p.kind}/${p.id}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studioKeys.all });
      // A reschedule can move an item in or out of a play's window.
      qc.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};
